import React, { lazy } from "react";
import { Navigate } from "react-router-dom";

// Service Reminder App — lazy loaded for code splitting
const Dashboard = lazy(() => import("../pages/Dashboard"));
const Customers = lazy(() => import("../pages/Customers"));
const Reminders = lazy(() => import("../pages/Reminders"));
const CalendarView = lazy(() => import("../pages/Calendar"));
// WhatsApp Inbox hidden for email-only MVP (page retained, route disabled below)
// const WhatsAppInbox = lazy(() => import("../pages/WhatsAppInbox"));
const NewServiceVisit = lazy(() => import("../pages/NewServiceVisit"));
const AddTaskRouter = lazy(() => import("../pages/AddTask"));
const Settings = lazy(() => import("../pages/Settings"));
const EmailTemplates = lazy(() => import("../pages/EmailTemplates"));
const Profile = lazy(() => import("../pages/Profile"));
const Notifications = lazy(() => import("../pages/Notifications"));
const Help = lazy(() => import("../pages/Help"));
const PlansAndSubscription = lazy(() => import("../pages/Plans"));
const BillingHistory = lazy(() => import("../pages/BillingHistory"));
const ServiceHistory = lazy(() => import("../pages/ServiceHistory"));
const AnalyticsDashboard = lazy(() => import("../pages/AnalyticsDashboard"));
const UserSupportTickets = lazy(() => import("../pages/SupportTickets"));
const CustomerPortal = lazy(() => import("../pages/CustomerPortal"));
const Referral = lazy(() => import("../pages/Referral"));
const Team = lazy(() => import("../pages/Team"));
const WebsiteBuilderPage = lazy(() => import("../pages/WebsiteBuilder"));
const MyWebsites = lazy(() => import("../pages/MyWebsites"));

// LeadWix 2.0 — Onboarding wizard (Phase 0)
const Onboarding = lazy(() => import("../pages/Onboarding"));
const GettingStarted = lazy(() => import("../pages/GettingStarted"));

// LeadWix 2.0 — Phase 1 (Leads, Pipeline, Widgets)
const Leads = lazy(() => import("../pages/Leads"));
const LeadDetail = lazy(() => import("../pages/Leads/LeadDetail"));
const LeadImport = lazy(() => import("../pages/Leads/Import"));
const Widgets = lazy(() => import("../pages/Widgets"));
const FormsList = lazy(() => import("../pages/Forms"));
const FormBuilder = lazy(() => import("../pages/Forms/Builder"));
const FormSubmissions = lazy(() => import("../pages/Forms/Submissions"));
const PublicForm = lazy(() => import("../pages/Forms/Public"));
const BookingsList    = lazy(() => import("../pages/Bookings"));
const BookingBuilder  = lazy(() => import("../pages/Bookings/Builder"));
const BookingsView    = lazy(() => import("../pages/Bookings/Bookings"));
const PublicBooking   = lazy(() => import("../pages/Bookings/Public"));

// LeadWix 2.0 — Phase 2 (Inbox, Sequences)
// Inbox (WhatsApp) hidden for email-only MVP (page retained, route disabled below)
// const Inbox = lazy(() => import("../pages/Inbox"));
const Sequences = lazy(() => import("../pages/Sequences"));
const Affiliate = lazy(() => import("../pages/Affiliate"));
// Authentication — lazy loaded
const Login = lazy(() => import("../pages/Authentication/Login"));
const OtpLogin = lazy(() => import("../pages/Authentication/OtpLogin"));
const ForgetPasswordPage = lazy(() => import("../pages/Authentication/ForgetPassword"));
const Logout = lazy(() => import("../pages/Authentication/Logout"));
const Register = lazy(() => import("../pages/Authentication/Register"));
const OAuthSuccess = lazy(() => import("../pages/Authentication/OAuthSuccess"));

// Landing Page — lazy loaded
const LandingPage = lazy(() => import("../pages/Landing/LandingRedesign"));
const Contact = lazy(() => import("../pages/Contact"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("../pages/RefundPolicy"));

// Blog — lazy loaded
const Blog = lazy(() => import("../pages/Blog"));
const BlogPost = lazy(() => import("../pages/Blog/Post"));

// Invoices — lazy loaded
const InvoiceList = lazy(() => import("../pages/Invoices/InvoiceList"));
const InvoiceCreate = lazy(() => import("../pages/Invoices/InvoiceCreate"));
const InvoiceDetails = lazy(() => import("../pages/Invoices/InvoiceDetails"));

// Docs — lazy loaded
const UserDocs = lazy(() => import("../pages/Docs/UserDocs"));
const AdminDocs = lazy(() => import("../pages/Docs/AdminDocs"));

// Admin Pages — lazy loaded
const AdminOverview = lazy(() => import("../pages/Admin/Overview"));
const AdminCustomerHealth = lazy(() => import("../pages/Admin/CustomerHealth"));
const TeamLeaderboard = lazy(() => import("../pages/Team/Leaderboard"));
const AdminUsers = lazy(() => import("../pages/Admin/Users"));
const AdminUserDetail = lazy(() => import("../pages/Admin/UserDetail"));
const AdminPayments = lazy(() => import("../pages/Admin/Payments"));
const AdminInvoices = lazy(() => import("../pages/Admin/Invoices"));
const AdminPlanUpgradeRequests = lazy(() => import("../pages/Admin/PlanUpgradeRequests"));
const AdminSupportTickets = lazy(() => import("../pages/Admin/SupportTickets"));
const AdminContactEnquiries = lazy(() => import("../pages/Admin/ContactEnquiries"));
const AdminSettings = lazy(() => import("../pages/Admin/Settings"));
const TermsOfUse = lazy(() => import("../pages/Pages/TermsOfUse"));
const AiWebsiteBuilderLanding = lazy(() => import("../pages/Landing/AiWebsiteBuilderLanding"));

const authProtectedRoutes = [
  // Service Reminder App
  { path: "/dashboard", component: <Dashboard /> },
  { path: "/index", component: <Dashboard /> },
  { path: "/customers", component: <Customers /> },
  { path: "/reminders", component: <Reminders /> },
  { path: "/calendar", component: <CalendarView /> },
  // { path: "/whatsapp-inbox", component: <WhatsAppInbox /> }, // hidden for email-only MVP
  { path: "/new-service-visit", component: <NewServiceVisit /> },
  { path: "/add-task", component: <AddTaskRouter /> },
  { path: "/settings", component: <Settings /> },
  { path: "/email-templates", component: <EmailTemplates /> },
  { path: "/profile", component: <Profile /> },
  { path: "/notifications", component: <Notifications /> },
  { path: "/help", component: <Help /> },
  { path: "/plans", component: <PlansAndSubscription /> },
  { path: "/billing", component: <BillingHistory /> },
  { path: "/service-history", component: <ServiceHistory /> },
  { path: "/analytics", component: <AnalyticsDashboard /> },
  { path: "/invoices", component: <InvoiceList /> },
  { path: "/invoices/create", component: <InvoiceCreate /> },
  { path: "/invoices/:id", component: <InvoiceDetails /> },
  { path: "/invoices/:id/edit", component: <InvoiceCreate /> },

  // Referral
  { path: "/referral", component: <Referral /> },

  // Team
  { path: "/team", component: <Team /> },
  { path: "/team/leaderboard", component: <TeamLeaderboard /> },

  // AI Website Builder
  { path: "/website-builder", component: <WebsiteBuilderPage /> },
  { path: "/my-websites", component: <MyWebsites /> },

  // LeadWix 2.0 — Onboarding (Phase 0)
  { path: "/onboarding", component: <Onboarding /> },
  { path: "/getting-started", component: <GettingStarted /> },

  // LeadWix 2.0 — Phase 1
  { path: "/leads", component: <Leads /> },
  { path: "/leads/import", component: <LeadImport /> },
  { path: "/leads/:id", component: <LeadDetail /> },
  { path: "/widgets", component: <Widgets /> },
  { path: "/forms", component: <FormsList /> },
  { path: "/forms/builder/:id", component: <FormBuilder /> },
  { path: "/forms/:id/submissions", component: <FormSubmissions /> },
  { path: "/bookings",                component: <BookingsList /> },
  { path: "/bookings/builder/:id",    component: <BookingBuilder /> },
  { path: "/bookings/:id/list",       component: <BookingsView /> },

  // LeadWix 2.0 — Phase 2
  // { path: "/inbox", component: <Inbox /> }, // hidden for email-only MVP
  { path: "/sequences", component: <Sequences /> },
  { path: "/affiliate", component: <Affiliate /> },

  // Support
  { path: "/support-tickets", component: <UserSupportTickets /> },

  // Documentation
  { path: "/docs", component: <UserDocs /> },

  // Admin Routes (require admin role)
  { path: "/admin", component: <AdminOverview /> },
  { path: "/admin/customer-health", component: <AdminCustomerHealth /> },
  { path: "/admin/users", component: <AdminUsers /> },
  { path: "/admin/users/:userId", component: <AdminUserDetail /> },
  { path: "/admin/payments", component: <AdminPayments /> },
  { path: "/admin/invoices", component: <AdminInvoices /> },
  { path: "/admin/plan-upgrade-requests", component: <AdminPlanUpgradeRequests /> },
  { path: "/admin/support-tickets", component: <AdminSupportTickets /> },
  { path: "/admin/contact-enquiries", component: <AdminContactEnquiries /> },
  { path: "/admin/settings", component: <AdminSettings /> },
  { path: "/admin/docs", component: <AdminDocs /> },

  // this route should be at the end of all other routes
  // eslint-disable-next-line react/display-name
  {
    path: "/",
    exact: true,
    component: <Navigate to="/dashboard" />,
  },
  { path: "*", component: <Navigate to="/dashboard" /> },
];

const publicRoutes = [
  // Home Page (Landing)
  { path: "/", component: <LandingPage /> },
  // Customer Portal
  { path: "/portal/:token", component: <CustomerPortal /> },
  // Blog
  { path: "/blog", component: <Blog /> },
  { path: "/blog/:slug", component: <BlogPost /> },
  // Authentication Page
  { path: "/logout", component: <Logout /> },
  { path: "/login", component: <Login /> },
  { path: "/otp-login", component: <OtpLogin /> },
  { path: "/login/success", component: <OAuthSuccess /> },
  { path: "/forgot-password", component: <ForgetPasswordPage /> },
  { path: "/register", component: <Register /> },
  { path: "/terms-of-use", component: <TermsOfUse /> },
  { path: "/landing_page", component: <LandingPage /> },
  { path: "/contact", component: <Contact /> },
  { path: "/privacy-policy", component: <PrivacyPolicy /> },
  { path: "/refund-policy", component: <RefundPolicy /> },
  { path: "/ai-website-builder", component: <AiWebsiteBuilderLanding /> },
  { path: "/f/:slug", component: <PublicForm /> },
  { path: "/b/:slug", component: <PublicBooking /> },
];

export { authProtectedRoutes, publicRoutes };